import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES } from "@src/appV2/api";
import {
  environmentConfig,
  isDevelopmentNodeEnvironment,
  isProductionNodeEnvironment,
} from "@src/appV2/environment";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";

import {
  LatestWorkerMovement,
  type TripStatus,
} from "../../../containers/facilityDashboard/ShiftCalendar/dayView/workerETA/types";
import {
  type DateInterval,
  type ShiftBoardShift,
  type ShiftBoardShiftLogs,
  type ShiftBoardTrip,
  type ShiftBoardWorker,
  type ShiftBoardWorkplace,
} from "../types";

const shiftBoardRequestSchema = z.object({
  sort: z.enum(["ascending", "descending"]).optional(),
  dateStart: z.string().datetime().optional(),
  dateEnd: z.string().datetime().optional(),
  qualifications: z.array(z.string()).optional(),
});

const datetimeRangeSchema = z.object({
  startAt: z.coerce.date().optional(),
  endAt: z.coerce.date().optional(),
});

const scheduleSchema = z.object({
  startAt: z.coerce.date(),
  endAt: z.coerce.date(),
  timeslot: z.enum(["am", "pm", "noc"]),
  extendBookabilityTo: z.coerce.date().optional(),
});

const shiftAttributes = z.object({
  requirements: z.object({
    qualificationName: z.string(),
  }),
  verified: z.boolean(),
  urgency: z.number(),
  clockInOutSchedule: datetimeRangeSchema,
  breakSchedule: datetimeRangeSchema,
  schedule: scheduleSchema,
  createdAt: z.coerce.date(),
  deleted: z.boolean().optional(),
  urgentlyBooked: z.boolean().optional(),
  isBillable: z.boolean().optional(),
  attendance: z
    .object({
      leftFacilityGeofence: z.boolean().optional(),
      arrivedFacilityGeofenceAt: z.coerce.date().optional(),
      leftFacilityGeofenceAt: z.coerce.date().optional(),
      isWorkerFarFromFacility: z.boolean().optional(),
    })
    .optional(),
});

const workplaceAttributes = z.object({
  name: z.string(),
  timezone: z.string(),
});

const workerAttributes = z.object({
  name: z.string(),
  profileImageUrl: z.string().optional(),
  phone: z.string(),
});

const shiftLogAttributes = z.object({
  performedBy: z
    .object({
      type: z.string().optional(),
      name: z.string().optional(),
    })
    .optional(),
  action: z.string(),
  subAction: z.string().optional(),
  eventAt: z.coerce.date().optional(),
  worker: z
    .object({
      id: z.string().optional(),
      name: z.string().optional(),
    })
    .optional(),
});

const shiftLogData = z.object({
  type: z.literal(API_TYPES.shiftLog),
  id: z.string(),
  attributes: shiftLogAttributes,
});

const shiftData = z.object({
  type: z.literal(API_TYPES.shift),
  id: z.string(),
  attributes: shiftAttributes,
  relationships: z.object({
    worker: z
      .object({
        data: z.object({
          type: z.literal(API_TYPES.worker),
          id: z.string(),
        }),
      })
      .optional(),
    trips: z
      .object({
        data: z.array(
          z.object({
            type: z.literal(API_TYPES.trip),
            id: z.string(),
          })
        ),
      })
      .optional(),
    workerLeftEarlyRequest: z
      .object({
        data: z.object({
          type: z.literal(API_TYPES.workerLeftEarlyRequest),
          id: z.string(),
        }),
      })
      .optional(),
    workplace: z.object({
      data: z.object({
        type: z.literal(API_TYPES.workplace),
        id: z.string(),
      }),
    }),
    shiftLogs: z
      .object({
        data: z.array(
          z.object({
            type: z.literal(API_TYPES.shiftLog),
            id: z.string(),
          })
        ),
      })
      .optional(),
  }),
});

const workplaceData = z.object({
  type: z.literal(API_TYPES.workplace),
  id: z.string(),
  attributes: workplaceAttributes,
});

const workerData = z.object({
  type: z.literal(API_TYPES.worker),
  id: z.string(),
  attributes: workerAttributes,
});

const workerLeftEarlyRequestData = z.object({
  type: z.literal(API_TYPES.workerLeftEarlyRequest),
  id: z.string(),
  attributes: z.object({
    shiftId: z.string(),
    workerId: z.string().optional(),
    replacementRequested: z.boolean(),
    leftWithPermission: z.boolean(),
    comment: z.string(),
    replacementShiftId: z.string().optional(),
    createdBy: z
      .object({
        userId: z.string().optional(),
        name: z.string().optional(),
        userType: z.string().optional(),
      })
      .optional(),
  }),
});

const LatestWorkerMovementSchema = z.enum(["MOVING", "STATIONARY", "UNKNOWN"]);

const TripStatusSchema = z.enum([
  "TRIP_STARTED",
  "TRIP_PENDING",
  "TRIP_APPROACHING",
  "TRIP_ARRIVED",
  "TRIP_COMPLETED",
  "TRIP_CANCELLED",
  "TRIP_EXPIRED",
  "TRIP_PLACEHOLDER",
]);

const TripProviderNameSchema = z.enum(["Radar", "HyperTrack"]);

const TripAttributesSchema = z.object({
  _id: z.string().optional(),
  workerId: z.string().optional(),
  shiftId: z.string().optional(),
  workplaceId: z.string().optional(),
  locationProviderTripId: z.string().optional(),
  // Optional (ALCH-6955): fact-only trips created at booking time carry no provider ETA/start —
  // mirrors the backend contract. A single required-field miss here rejects the whole shift-board
  // response (the trip sits in the `included` union), blanking the board.
  currentEtaDuration: z.number().optional(),
  currentEtaDistance: z.number().optional(),
  initialEtaDuration: z.number().optional(),
  initialEtaDistance: z.number().optional(),
  latestWorkerMovement: LatestWorkerMovementSchema.optional(),
  currentLatitude: z.number().optional(),
  currentLongitude: z.number().optional(),
  status: TripStatusSchema,
  hasWorkerApproachingNotificationBeenSent: z.boolean(),
  hasWorkerRunningLateNotificationBeenSent: z.boolean(),
  tripStartTime: z.coerce.date().optional(),
  tripStopTime: z.coerce.date().optional(),
  workerDeclaredEta: z.coerce.date().optional(),
  providerName: TripProviderNameSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  lastLocationReceivedByServerAt: z.coerce.date().optional(),
  trackingUrl: z.string().url().optional(),
});

export const TripResponseAttributesSchema = TripAttributesSchema.extend({
  isLate: z.boolean().default(false),
});

const tripData = z.object({
  type: z.literal(API_TYPES.trip),
  id: z.string(),
  attributes: TripResponseAttributesSchema,
});

const shiftBoardResponseSchema = z.object({
  data: z.array(shiftData),
  included: z.array(
    z.union([workplaceData, workerData, tripData, workerLeftEarlyRequestData, shiftLogData])
  ),
});

function convertWorkplace(workplace: z.infer<typeof workplaceData>): ShiftBoardWorkplace {
  return {
    id: workplace.id,
    name: workplace.attributes.name,
    timeZone: workplace.attributes.timezone,
  };
}

function convertTrip(trip: z.infer<typeof tripData>): ShiftBoardTrip {
  return {
    id: trip.id,
    eta: {
      distanceInM: trip.attributes.currentEtaDistance,
      durationInMin: trip.attributes.currentEtaDuration,
    },
    initialEta: {
      distanceInM: trip.attributes.initialEtaDistance,
      durationInMin: trip.attributes.initialEtaDuration,
    },
    status: trip.attributes.status as TripStatus,
    workerDeclaredEta: trip.attributes.workerDeclaredEta,
    latestWorkerMovement: (trip.attributes.latestWorkerMovement ??
      LatestWorkerMovement.UNKNOWN) as LatestWorkerMovement,
    lastLocationReceivedByServerAt: trip.attributes.lastLocationReceivedByServerAt,
    trackingUrl: trip.attributes.trackingUrl,
    isLate: trip.attributes.isLate ?? false,
  };
}

function convertWorker(worker: z.infer<typeof workerData>): ShiftBoardWorker {
  return {
    id: worker.id,
    name: worker.attributes.name,
    avatarUrl: worker.attributes?.profileImageUrl,
  };
}

function convertWorkerLeftEarlyRequest(request: z.infer<typeof workerLeftEarlyRequestData>) {
  return {
    id: request.id,
    shiftId: request.attributes.shiftId,
    workerId: request.attributes.workerId,
    leftWithPermission: request.attributes.leftWithPermission,
    replacementRequested: request.attributes.replacementRequested,
    comment: request.attributes.comment,
    replacementShiftId: request.attributes.replacementShiftId,
    createdBy: request.attributes.createdBy,
  };
}

function convertShiftLog(shiftLog: z.infer<typeof shiftLogData>): ShiftBoardShiftLogs {
  return {
    id: shiftLog.id,
    performedBy: shiftLog.attributes.performedBy
      ? {
          type: shiftLog.attributes.performedBy.type ?? undefined,
          name: shiftLog.attributes.performedBy.name ?? undefined,
        }
      : undefined,
    action: shiftLog.attributes.action,
    subAction: shiftLog.attributes.subAction,
    eventAt: shiftLog.attributes.eventAt,
    worker: shiftLog.attributes.worker
      ? {
          id: shiftLog.attributes.worker.id,
          name: shiftLog.attributes.worker.name ?? undefined,
        }
      : undefined,
  };
}

export function useShiftBoardData(params: { token: string; dateInterval?: DateInterval }): {
  workplace: ShiftBoardWorkplace;
  shifts: ShiftBoardShift[];
} {
  const { token, dateInterval } = params;

  const queryParams = {
    dateStart: dateInterval?.start.toISOString(),
    dateEnd: dateInterval?.end.toISOString(),
    sort: "ascending" as const,
  };

  shiftBoardRequestSchema.parse(queryParams);

  const url = `${environmentConfig.REACT_APP_BASE_API_URL}/shift-board`;
  const { data } = useQuery({
    queryKey: [url, queryParams],
    queryFn: async () => {
      const response = await axios.get(url, {
        params: queryParams,
        headers: { Authorization: token, "Content-Type": "application/json" },
      });
      // Validate response schema
      if (isDevelopmentNodeEnvironment()) {
        try {
          return shiftBoardResponseSchema.parse(response.data);
        } catch (error) {
          throw new Error(`${String(error)}: ${JSON.stringify({ url, queryParams })}`);
        }
      } else if (isProductionNodeEnvironment()) {
        const result = await shiftBoardResponseSchema.safeParseAsync(response.data);
        if (!result.success) {
          logEvent(APP_V2_APP_EVENTS.API_SCHEMA_VALIDATION_ERROR, {
            errors: result.error.errors,
            metadata: { url, method: "GET" },
          });
        }

        return result.data;
      }

      return undefined;
    },
    refetchInterval: 60_000,
    suspense: true,
  });

  const workplacesMap =
    data?.included
      ?.filter((item): item is z.infer<typeof workplaceData> => item.type === API_TYPES.workplace)
      .reduce(
        (accumulator, workplace) => {
          accumulator[workplace.id] = convertWorkplace(workplace);
          return accumulator;
        },
        {} as Record<string, ShiftBoardWorkplace>
      ) ?? {};
  // Assume the correct workplace is the first one
  const workplace = Object.values(workplacesMap)[0];
  if (!workplace) {
    throw new Error(`No workplace found in response`);
  }

  const tripsMap =
    data?.included
      ?.filter((item): item is z.infer<typeof tripData> => item.type === API_TYPES.trip)
      .reduce(
        (accumulator, trip) => {
          accumulator[trip.id] = convertTrip(trip);
          return accumulator;
        },
        {} as Record<string, ShiftBoardTrip>
      ) ?? {};

  const workersMap =
    data?.included
      ?.filter((item): item is z.infer<typeof workerData> => item.type === API_TYPES.worker)
      .reduce(
        (accumulator, worker) => {
          accumulator[worker.id] = convertWorker(worker);
          return accumulator;
        },
        {} as Record<string, ShiftBoardWorker>
      ) ?? {};

  const workerLeftEarlyRequestsMap =
    data?.included
      ?.filter(
        (item): item is z.infer<typeof workerLeftEarlyRequestData> =>
          item.type === API_TYPES.workerLeftEarlyRequest
      )
      .reduce(
        (accumulator, request) => {
          accumulator[request.id] = convertWorkerLeftEarlyRequest(request);
          return accumulator;
        },
        {} as Record<string, ReturnType<typeof convertWorkerLeftEarlyRequest>>
      ) ?? {};

  const shiftLogsMap =
    data?.included
      ?.filter((item): item is z.infer<typeof shiftLogData> => item.type === API_TYPES.shiftLog)
      .reduce(
        (accumulator, shiftLog) => {
          accumulator[shiftLog.id] = convertShiftLog(shiftLog);
          return accumulator;
        },
        {} as Record<string, ShiftBoardShiftLogs>
      ) ?? {};

  const shifts =
    data?.data
      ?.filter((item): item is z.infer<typeof shiftData> => item.type === "shift")
      .map((shiftData) => {
        const tripData = shiftData.relationships.trips?.data.at(-1);
        const trip = tripData && tripsMap[tripData.id];
        // TODO: fix the worker.id return in the API
        const worker =
          shiftData.relationships.worker && workersMap[shiftData.relationships.worker.data.id];

        const logs: ShiftBoardShiftLogs[] =
          shiftData.relationships.shiftLogs?.data
            .map((logData) => shiftLogsMap[logData.id])
            .filter(isDefined) ?? [];

        const shift: ShiftBoardShift = {
          id: shiftData.id,
          start: shiftData.attributes.schedule.startAt,
          end: shiftData.attributes.schedule.endAt,
          timeslot: shiftData.attributes.schedule.timeslot,
          clockInOut: {
            clockIn: shiftData.attributes.clockInOutSchedule.startAt,
            clockOut: shiftData.attributes.clockInOutSchedule.endAt,
          },
          qualification: shiftData.attributes.requirements.qualificationName,
          verified: shiftData.attributes.verified,
          deleted: shiftData.attributes.deleted ?? false,
          isBillable: shiftData.attributes.isBillable ?? true,
          urgency: shiftData.attributes.urgency,
          urgentlyBooked: shiftData.attributes.urgentlyBooked,
          extendBookabilityTo: shiftData.attributes.schedule.extendBookabilityTo,
          workerLeftEarlyRequest: shiftData.relationships.workerLeftEarlyRequest?.data.id
            ? workerLeftEarlyRequestsMap[shiftData.relationships.workerLeftEarlyRequest.data.id]
            : undefined,
          attendance: shiftData.attributes.attendance
            ? {
                leftFacilityGeofence: shiftData.attributes.attendance.leftFacilityGeofence,
                arrivedFacilityGeofenceAt:
                  shiftData.attributes.attendance.arrivedFacilityGeofenceAt,
                leftFacilityGeofenceAt: shiftData.attributes.attendance.leftFacilityGeofenceAt,
                isWorkerFarFromFacility: shiftData.attributes.attendance.isWorkerFarFromFacility,
              }
            : undefined,
          worker,
          trip,
          logs,
        };
        return shift;
      }) ?? [];

  return {
    workplace,
    shifts,
  };
}
